




























































































































































































import {
  Component,
  Inject,
  Watch,
  Prop,
  Ref,
  Mixins
} from "vue-property-decorator";

// Mixins
import HasValidation from "@/mixins/HasValidation";
import Context from "@/mixins/context";

// Enums/Data
import { DataModules } from "@/store/modules/data";

// Types
import {
  IBasketProviderData,
  IBasketProviderMethods
} from "@/models/providers/baskets";
import {
  IProvisionProviderData,
  IProvisionProviderMethods
} from "@/models/providers/provisioning";
import { IUploadFile } from "@/models/uploadFiles";
import CustomFields from "@/components/app/global/customFields/customFields.vue";
import { IBasketConvertForm } from "@/models/baskets";

@Component({
  components: {
    "basket-items": () =>
      import(
        "@/components/app/global/baskets/basket/shared/onePageBasketItems.vue"
      ),
    "basket-control": () =>
      import(
        "@/components/app/global/baskets/basket/basketView/basketControl.vue"
      ),
    "billable-entities": () =>
      import("@/components/app/global/client/billableEntities.vue"),
    CustomFields,
    "convert-fields": () =>
      import(
        "@/components/app/global/baskets/basket/basketView/convertFields.vue"
      ),
    "provision-warnings": () =>
      import(
        "@/components/app/global/baskets/basket/shared/provisionWarnings.vue"
      ),
    "basket-currency-switcher": () =>
      import(
        "@/components/app/global/baskets/basket/basketCurrencySwitcher.vue"
      ),
    "billable-entities-provider": () =>
      import("@/components/app/global/client/billableEntitiesProvider.vue"),
    "cprod-billable-entity-address-search": () =>
      import("@/components/app/global/client/addressSearch.vue"),
    "billable-entities-control": () =>
      import("@/components/app/global/client/billableEntitiesControl.vue")
  }
})
export default class BasketBasketView extends Mixins(HasValidation, Context) {
  // Inject

  @Inject({ from: "basketProviderData" })
  public readonly $providerData!: () => IBasketProviderData;

  @Inject({ from: "basketProviderMethods" })
  public readonly $providerMethods!: IBasketProviderMethods;

  @Inject({ from: "getConvertForm" })
  public readonly getConvertForm!: () => IBasketConvertForm;

  // Refs

  @Ref("basketCustomFields")
  public basketCustomFields!: CustomFields;

  public get basketMethods() {
    return this.$providerMethods;
  }

  // Inject Provisioning Provider
  @Inject({ from: "provisionProviderData" })
  public readonly $provisionProviderData!: () => IProvisionProviderData;

  public get provisionData() {
    return this.$provisionProviderData();
  }

  // Inject Provisioning Provider
  @Inject({ from: "provisionProviderMethods" })
  public readonly $provisionProviderMethods!: IProvisionProviderMethods;

  public get provisionMethods() {
    return this.$provisionProviderMethods;
  }

  // Props

  @Prop({ required: true })
  public isNewOrder!: boolean;

  // Data

  public customFieldsDataModule: string = DataModules.CUSTOM_FIELDS_BASKET;

  public isLoading = true;
  public isReloading = false;
  public isProcessing = false;
  public requestError = false;

  public customImages: IUploadFile[] = [];
  public basketNote = "";

  // Life-cycle methods

  public created() {
    this.loadData();
  }

  // Computed

  public get basketData() {
    return this.$providerData();
  }

  public get basket() {
    return this.basketData.basket;
  }

  public get hasRecurringProducts() {
    return this.basketData.hasRecurringProducts;
  }

  public get isManualImport() {
    return this.getConvertForm().manual_import;
  }

  public get hasRequiredBillingDetails() {
    return (
      this.basketData.isAddressRequiredAtCheckout &&
      !this.basketData.basket.address_id
    );
  }

  // Methods

  public selectProduct() {
    this.$emit("select-product");
  }

  public async loadData() {
    try {
      if (this.basketData.isNewBasket) return;
      await this.provisionMethods.checkConfiguration(this.basketData.basketId);
      this.customImages = this.basketData.customImages;
      this.requestError = false;
    } catch (error) {
      if (
        this.$store.getters["api/is403"](error) ||
        this.$store.getters["api/is401"](error)
      ) {
        this.basketMethods.forceBasketReset();
      }
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isReloading = false;
      this.isLoading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public async validateBeforeSubmit() {
    try {
      this.isProcessing = true;
      this.basketMethods.set("isProcessing", true);

      if (!(await this.validate())) {
        throw this.$t("_sentence.form_is_not_valid");
      }

      if (
        !this.isGuest &&
        this.provisionData.hasProvisioning &&
        !this.$_.isEmpty(this.provisionData.provisionErrors)
      ) {
        throw this.$t("_sentence.please_configure_fields_with_warning");
      }

      await this.saveBasketPageData();

      return true;
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
      return false;
    } finally {
      this.isProcessing = false;
      this.basketMethods.set("isProcessing", false);
    }
  }

  public get hasUnsavedImages() {
    return this.$_.some(
      this.customImages,
      image => !image.hash && !this.$_.isEmpty(image.file)
    );
  }

  public async saveBasketPageData() {
    if (this.hasUnsavedImages) {
      await this.basketCustomFields.uploadCustomImages(this.isGuest);
      this.customImages = [];
      this.basketMethods.saveCustomImages(this.customImages);
    }

    if (
      !this.$_.isEqual(
        this.basketData.basketForm,
        this.basketData.basketFormClone
      )
    ) {
      await this.basketMethods.updateBasketData(this.basketData.basketForm);
      await this.basketMethods.getBasket(true);
    }
  }

  public handleImageUploadError(error) {
    if (
      !this.$store.getters["api/is"](error, 422) ||
      !this.$_.get(error, "response.config.url").includes("/image")
    ) {
      return;
    }

    const pathParams = this.$_.get(error, "response.config.url").split("/");
    const fieldId = this.$_.get(pathParams, `[${pathParams.length - 2}]`);

    this.basketMethods.saveCustomImages(
      this.$_.filter(this.customImages, image => image.id !== fieldId)
    );
  }

  @Watch("customImages")
  public onCustomImagesChange() {
    this.basketMethods.saveCustomImages(this.customImages);
  }
}
